/* eslint-disable no-undef */
import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Button, Badge } from 'react-bootstrap';
import { fetchWrapper } from '../../../../../../../../_helpers/fetchWrapper';
import { FaEye } from 'react-icons/fa';
import ToolTip from '../../../../../../../../Components/ToolTip';
import UserImage from '../../../../../../../../Components/UserImage';
import Swal from 'sweetalert2';
import { ErrorMessageHandler, toNepaliNumber } from '../../../../../../../../_helpers/_methods';
import { usePagination, useRowSelect, useTable, useFilters, useAsyncDebounce } from 'react-table';
import { checkPermission } from '../../../../../../../../_helpers/roles/check-roles';
import DownloadBtn from '../../../../../../../../Components/Buttons/DownloadBtn';
import ReactTableRender from '../../../../../../../../Components/ReactTable/ReactTableRender';
import DefaultColumnFilter from '../../../../../../../../Components/ReactTable/Filters/DefaultColumnFilter';

function SelectedParticipantsTab() {
  const { id } = useParams();
  const intl = useIntl();
  const isPublicView = localStorage.getItem('eventToken');
  const local = useSelector((state) => state.local.local);
  const activeRole = useSelector((state) => state.user?.user?.active_role);

  const initialState = useMemo(() => {
    if (isPublicView === 'undefined') {
      return {};
    } else {
      return {
        hiddenColumns: ['actions']
      };
    }
  }, [isPublicView]);

  const [pageNum, setPageNum] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [nameSearch, setNameSearch] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);

  const ManualFilterByName = ({ column: { filterValue, setFilter } }) => {
    const inputRef = useRef(null);
    const [name, setName] = useState(filterValue);
    const handleNameChange = useAsyncDebounce((value) => {
      setFilter(value || undefined);
      setNameSearch(value);
    }, 800);
    return (
      <input
        ref={inputRef}
        value={name || ''}
        onChange={(e) => {
          setName(e.target.value);
          handleNameChange(e.target.value);
        }}
        placeholder={intl.formatMessage({ id: 'filter.search' })}
      />
    );
  };
  const ManualFilterByPhone = ({ column: { filterValue, setFilter } }) => {
    const inputRef = useRef(null);
    const [phone, setPhone] = useState(filterValue);
    const handlePhoneChange = useAsyncDebounce((phone) => {
      setFilter(phone || undefined);
      setPhoneSearch(phone);
    }, 800);
    return (
      <input
        ref={inputRef}
        type="number"
        value={phone || ''}
        onChange={(e) => {
          setPhone(e.target.value);
          handlePhoneChange(e.target.value);
        }}
        placeholder={intl.formatMessage({ id: 'filter.search' })}
      />
    );
  };

  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter
    }),
    []
  );
  const SelectColumnFilter = ({ column: { filterValue, setFilter, preFilteredRows, id } }) => {
    // Calculate the options for filtering
    // using the preFilteredRows
    const options = useMemo(() => {
      const options = new Set();
      if (preFilteredRows) {
        preFilteredRows.forEach((row) => {
          options.add(row.values[id]);
        });
        return [...options.values()];
      }
      return [];
    }, [id, preFilteredRows]);

    // Render a multi-select box
    return (
      <select
        value={filterValue}
        onChange={(e) => {
          setFilter(e.target.value || undefined);
        }}>
        <FormattedMessage id="tableSelect.all">
          {(label) => <option value="">{label[0]}</option>}
        </FormattedMessage>
        {options.map((option, i) => (
          <option key={i} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  };

  const onRemoveClick = (profileId) => {
    Swal.fire({
      title: intl.formatMessage({ id: 'are_you_sure' }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: intl.formatMessage({ id: 'yes' }),
      cancelButtonText: intl.formatMessage({ id: 'cancel' }),
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        handleParticipantRemove(profileId);
      }
    });
  };

  const handleParticipantRemove = async (profileId) => {
    try {
      await fetchWrapper.post(`/event/${id}/remove-participants/`, {
        participants: [profileId]
      });
      getParticipantTableData();

      toast.success(<FormattedMessage id="event.participantRemoved" />);
    } catch (error) {
      console.log(error);
      ErrorMessageHandler(error);
      toast.error('Something went wrong');
    }
  };
  const getParticipantTableData = async () => {
    try {
      setLoading(true);
      let url = `/event/${id}/participants/`;
      if (pageNum) {
        let offSet = (pageNum - 1) * pageSize;
        if (offSet === 0) {
          url += `?page=${pageNum}&limit=${pageSize}`;
        } else {
          url += `?offset=${offSet}&page=${pageNum}&limit=${pageSize}`;
        }
      } else if (pageSize) {
        url += `?limit=${pageSize}`;
      }
      if (nameSearch) {
        url += `&fullname=${nameSearch}`;
      }
      if (phoneSearch) {
        params += `&phone=${phoneSearch}`;
      }
      url += '&selected=true';

      const response = await fetchWrapper.get(url);
      const { data } = response;
      const { count, results } = data;
      setSelectedParticipants(results);
      setTotalCount(count);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      ErrorMessageHandler(err);
    }
  };

  useEffect(() => {
    getParticipantTableData();
  }, [pageNum, pageSize, phoneSearch, nameSearch]);

  const columns = useMemo(
    () => [
      {
        Header: <FormattedMessage id="sn" />,
        accessor: 'serial_number',
        disableFilters: true,
        width: '40px'
      },
      {
        Header: <FormattedMessage id="event.userFullName" />,
        accessor: 'fullname',
        Filter: ManualFilterByName,
        Cell: ({ row: { original }, cell: { value } }) => {
          return (
            <Link
              to={isPublicView ? '#' : `/admin/users/trainees/profile/${original.id}`}
              className={`d-flex align-items-center ${isPublicView ? 'pointer-default' : ''}`}>
              <div className="me-1 position-relative">
                <UserImage avatar={original.image} userName={value} userId={String(original.id)} />
                <span className="avatar-profile-iscomplete-badge">
                  {original.warnings && (
                    <ToolTip
                      warnings={original.warnings}
                      isComplete={original.is_profile_updated}
                    />
                  )}
                </span>
              </div>
              <span className="d-block">
                {value} <br />
                <small>{original.office_name && original.office_name}</small>
              </span>
              {original.suspended && <Badge bg="danger">Suspended</Badge>}
            </Link>
          );
        },
        width: '170px'
      },
      {
        Header: <FormattedMessage id="event.userPhone" />,
        accessor: 'mobile_number',
        Filter: ManualFilterByPhone,
        width: '100px'
      },
      {
        Header: <FormattedMessage id="event.userAddress" />,
        accessor: 'address',
        width: '170px'
      },
      {
        Header: <FormattedMessage id="event.gender" />,
        accessor: 'gender',
        Filter: SelectColumnFilter,
        filter: 'equals',
        width: '50px'
      },
      {
        Header: <FormattedMessage id="event.ethnicity" />,
        accessor: 'ethnicity',
        Cell: ({ cell: { value } }) => {
          return <span>{value}</span>;
        },
        width: '120px'
      },
      {
        Header: <FormattedMessage id="event.participantType" />,
        accessor: 'participant_type',
        Filter: SelectColumnFilter,
        width: '100px'
      },
      {
        Header: <FormattedMessage id="event.officeName" />,
        accessor: 'office_name'
      },
      {
        Header: <FormattedMessage id="actions" />,
        accessor: 'actions',
        disableFilters: true,
        Cell: ({ row: { original } }) => {
          return (
            <span className="d-flex align-items-center">
              {['superuser', 'cd_admin', 'executive'].includes(activeRole) ? (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => onRemoveClick(original.id)}>
                  <FormattedMessage id="button.remove" />
                </Button>
              ) : (
                <Link to={`/admin/users/trainees/profile/${original.id}`}>
                  <Button variant="outline-primary">
                    <FaEye />
                  </Button>
                </Link>
              )}
            </span>
          );
        }
      }
    ],
    [activeRole, local]
  );

  const data = useMemo(() => {
    if (selectedParticipants) {
      let count = 1;
      return selectedParticipants.map((result) => {
        return {
          serial_number: local === 'ne' ? toNepaliNumber(count++) : count++,
          fullname: result.fullname,
          gender: intl.formatMessage({ id: result.gender || 'p' }),
          mobile_number: result.phone ? result.phone : 'not availiable',
          address: result.address,
          ethnicity: result.ethenicity?.title,
          participant_type: result.employee_type_title?.join(', '),
          office_name: result.office_name,
          id: result.id,
          is_profile_updated: result.is_profile_updated,
          warnings: result.warnings,
          image: result.image ? process.env.REACT_APP_BASE_URL + result.image : ''
        };
      });
    } else {
      return [];
    }
  }, [selectedParticipants]);

  const {
    headerGroups,
    getTableProps,
    getTableBodyProps,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    previousPage
  } = useTable(
    {
      columns,
      data: data,
      manualPagination: true,
      pageCount: pageNum,
      defaultCanFilter: false,
      defaultColumn,
      initialState: { ...initialState }
    },
    useFilters,
    usePagination,
    useRowSelect
  );
  const pageCountArray = useMemo(
    () => Array.from({ length: Math.ceil(totalCount / pageSize) }, (v, i) => i + 1),
    [totalCount, pageSize]
  );

  const nextPage = (page) => {
    setPageNum(page);
  };
  const gotoPage = (page) => {
    setPageNum(page);
  };

  return (
    <ReactTableRender
      getTableProps={getTableProps}
      headerGroups={headerGroups}
      getTableBodyProps={getTableBodyProps}
      page={page}
      prepareRow={prepareRow}
      totalCount={totalCount}
      gotoPage={gotoPage}
      previousPage={previousPage}
      pageCount={pageCount}
      pageCountArray={pageCountArray}
      pageNum={pageNum}
      showGoto={true}
      nextPage={nextPage}
      canNextPage={canNextPage}
      canPreviousPage={canPreviousPage}
      loading={loading}
      setPageSize={setPageSize}
      rightChildren={
        <div className="d-flex ps-4 justify-content-end" style={{ width: '50em' }}>
          {checkPermission(activeRole, 'export files') && (
            <>
              <DownloadBtn
                apiUrl={`/report/participant-report/?event_id=${id}&type=excel`}
                type="excel"
              />
              <DownloadBtn
                apiUrl={`/report/participant-report/?type=pdf&event_id=${id}`}
                type="pdf"
              />
            </>
          )}
        </div>
      }
    />
  );
}

export default SelectedParticipantsTab;
